'use client';

/**
 * DocumentAttributesPanel Component
 *
 * Right-panel component for the unified document view page.
 * Displays configurable attribute sections using PanelSection and PanelAttributeList.
 *
 * Each portal passes different combinations of props:
 * - Consumer: findings (with confidence), caseLinks (full), rejectionInfo
 * - Business: findings (no confidence details), tags
 * - Admin: adminMetadata (gcsPath, chunkCount, raw JSON), tags
 *
 * Issue #1163
 */

import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LabelIcon from '@mui/icons-material/Label';
import LinkIcon from '@mui/icons-material/Link';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorageIcon from '@mui/icons-material/Storage';
import { Box, Skeleton, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { formatBytes, formatDateTime, snakeToTitle } from '../../utils/formatters';
import { GlassAlert } from '../GlassAlert/GlassAlert';
import { GlassChip } from '../GlassChip/GlassChip';
import type { PanelAttribute } from '../SplitPanel/PanelAttributeList';
import { PanelAttributeList } from '../SplitPanel/PanelAttributeList';
import { PanelSection } from '../SplitPanel/PanelSection';
import { SplitPanelContent } from '../SplitPanel/SplitPanelContent';
import { SplitPanelFooter } from '../SplitPanel/SplitPanelFooter';
import { SplitPanelHeader } from '../SplitPanel/SplitPanelHeader';
import { ConfidenceBadge } from './ConfidenceBadge';
import { RejectionBanner } from './RejectionBanner';

// ============================================================================
// Types
// ============================================================================

export interface DocumentViewData {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  updatedAt?: string;
  documentType?: string;
  category?: string;
  processingStatus: string;
  approvalStatus?: string;
}

export interface DocumentViewFinding {
  id: string;
  title: string;
  confidence: number;
  extractedValue?: string;
}

export interface DocumentViewCaseLink {
  caseId: string;
  caseNumber: string;
  verificationStatus: string;
}

export interface DocumentViewRejection {
  reason: string;
  variant: 'rejected' | 'needs_reupload';
  onReupload?: () => void;
}

export interface DocumentViewAdminMetadata {
  gcsPath: string;
  chunkCount: number;
  documentId: string;
  rawMetadata: Record<string, unknown>;
}

export interface DocumentAttributesPanelProps {
  document: DocumentViewData;
  findings?: { items: DocumentViewFinding[]; loading?: boolean };
  caseLinks?: { items?: DocumentViewCaseLink[]; count?: number; mode: 'full' | 'count' };
  tags?: string[];
  adminMetadata?: DocumentViewAdminMetadata;
  rejectionInfo?: DocumentViewRejection;
  actions?: ReactNode;
  onClose?: () => void;
  formatFileSize?: (bytes: number) => string;
  formatDate?: (date: string) => string;
}

// ============================================================================
// Helpers
// ============================================================================

type StatusColor = 'success' | 'warning' | 'error' | 'default';

function getStatusColor(status: string): StatusColor {
  const normalized = status.toLowerCase();
  if (['completed', 'verified', 'approved'].includes(normalized)) return 'success';
  if (['processing', 'pending'].includes(normalized)) return 'warning';
  if (['failed', 'rejected'].includes(normalized)) return 'error';
  return 'default';
}

// ============================================================================
// Component
// ============================================================================

export function DocumentAttributesPanel({
  document,
  findings,
  caseLinks,
  tags,
  adminMetadata,
  rejectionInfo,
  actions,
  onClose,
  formatFileSize = formatBytes,
  formatDate = formatDateTime,
}: DocumentAttributesPanelProps) {
  const { t } = useI18n();

  // --- File Information attributes ---
  const fileAttributes: PanelAttribute[] = [
    { label: t('documents.attributes.name'), value: document.name, icon: <DescriptionIcon /> },
    { label: t('documents.attributes.type'), value: document.mimeType },
    { label: t('documents.attributes.size'), value: formatFileSize(document.size) },
    { label: t('documents.attributes.uploaded'), value: formatDate(document.uploadedAt) },
    {
      label: t('documents.attributes.lastUpdated'),
      value: document.updatedAt ? formatDate(document.updatedAt) : t('common.na'),
      hidden: !document.updatedAt,
    },
    {
      label: t('documents.attributes.category'),
      value: document.category ?? t('common.na'),
      icon: <CategoryIcon />,
      hidden: !document.category,
    },
    {
      label: t('documents.attributes.documentType'),
      value: document.documentType ?? t('common.na'),
      hidden: !document.documentType,
    },
  ];

  // --- Status chips ---
  const processingColor = getStatusColor(document.processingStatus);
  const approvalColor = document.approvalStatus ? getStatusColor(document.approvalStatus) : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {onClose && <SplitPanelHeader title={t('documents.attributes.title')} onClose={onClose} />}
      <SplitPanelContent>
        {/* Rejection Banner */}
        {rejectionInfo && (
          <RejectionBanner
            reason={rejectionInfo.reason}
            variant={rejectionInfo.variant}
            onReupload={rejectionInfo.onReupload}
          />
        )}

        {/* File Information */}
        <PanelSection title={t('documents.sections.fileInfo')} icon={<FolderIcon />} defaultExpanded divider>
          <PanelAttributeList attributes={fileAttributes} compact />
        </PanelSection>

        {/* Status */}
        {/* Status — only render when processingStatus is available */}
        {document.processingStatus && (
          <PanelSection title={t('documents.sections.status')} icon={<InfoOutlinedIcon />} defaultExpanded divider>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
              <GlassChip
                label={snakeToTitle(document.processingStatus)}
                color={processingColor}
                size="small"
                variant="filled"
              />
              {document.approvalStatus && approvalColor && (
                <GlassChip
                  label={snakeToTitle(document.approvalStatus)}
                  color={approvalColor}
                  size="small"
                  variant="filled"
                />
              )}
            </Box>
          </PanelSection>
        )}

        {/* AI Findings */}
        {findings && (
          <PanelSection title={t('documents.sections.aiFindings')} icon={<PsychologyIcon />} defaultExpanded divider>
            {findings.loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={36} />
                ))}
              </Box>
            ) : findings.items.length === 0 ? (
              <GlassAlert severity="info" sx={{ mt: 1 }}>
                {t('documents.findings.none')}
              </GlassAlert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                {findings.items.map((finding) => (
                  <Box
                    key={finding.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {finding.title}
                      </Typography>
                      {finding.extractedValue && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {finding.extractedValue}
                        </Typography>
                      )}
                    </Box>
                    <ConfidenceBadge confidence={finding.confidence} size="small" />
                  </Box>
                ))}
              </Box>
            )}
          </PanelSection>
        )}

        {/* Linked Cases */}
        {caseLinks && (caseLinks.items?.length ?? caseLinks.count ?? 0) > 0 && (
          <PanelSection title={t('documents.sections.linkedCases')} icon={<LinkIcon />} defaultExpanded divider>
            {caseLinks.mode === 'full' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                {(caseLinks.items ?? []).map((link) => (
                  <Box
                    key={link.caseId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {link.caseNumber}
                    </Typography>
                    <GlassChip
                      label={snakeToTitle(link.verificationStatus)}
                      color={getStatusColor(link.verificationStatus)}
                      size="small"
                      variant="filled"
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  py: 1,
                }}
              >
                {t('documents.caseLinks.count', { count: caseLinks.count ?? caseLinks.items?.length ?? 0 })}
              </Typography>
            )}
          </PanelSection>
        )}

        {/* Tags */}
        {tags && tags.length > 0 && (
          <PanelSection title={t('documents.sections.tags')} icon={<LabelIcon />} defaultExpanded divider>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, py: 1 }}>
              {tags.map((tag) => (
                <GlassChip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          </PanelSection>
        )}

        {/* Storage & Metadata (admin only) */}
        {adminMetadata && (
          <PanelSection title={t('documents.sections.storage')} icon={<StorageIcon />} defaultExpanded={false}>
            <PanelAttributeList
              attributes={[
                {
                  label: t('documents.attributes.gcsPath'),
                  value: adminMetadata.gcsPath,
                  copyable: true,
                  copyValue: adminMetadata.gcsPath,
                },
                {
                  label: t('documents.attributes.chunkCount'),
                  value: String(adminMetadata.chunkCount),
                },
                {
                  label: t('documents.attributes.documentId'),
                  value: adminMetadata.documentId,
                  copyable: true,
                  copyValue: adminMetadata.documentId,
                },
              ]}
              compact
            />
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                {t('documents.attributes.rawMetadata')}
              </Typography>
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  maxHeight: 200,
                  overflow: 'auto',
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  m: 0,
                }}
              >
                {JSON.stringify(adminMetadata.rawMetadata, null, 2)}
              </Box>
            </Box>
          </PanelSection>
        )}
      </SplitPanelContent>
      {/* Actions slot */}
      {actions && <SplitPanelFooter>{actions}</SplitPanelFooter>}
    </Box>
  );
}

export default DocumentAttributesPanel;
